// ============================================================================
// COA GROUP SERVICE (CRUD, entity-scoped via X-Entity-ID header)
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { coaGroupEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  CoaGroupDto,
  CreateCoaGroupRequest,
  UpdateCoaGroupRequest,
  CoaGroupQueryParams,
} from '../types';

class CoaGroupService {
  async getAll(params?: CoaGroupQueryParams): Promise<PaginatedResponse<CoaGroupDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(coaGroupEndpoints.list(), params)
      : coaGroupEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<CoaGroupDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<CoaGroupDto>> {
    const response = await get<ApiResponse<CoaGroupDto>>(coaGroupEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateCoaGroupRequest): Promise<ApiResponse<CoaGroupDto>> {
    const response = await post<ApiResponse<CoaGroupDto>>(coaGroupEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCoaGroupRequest): Promise<ApiResponse<CoaGroupDto>> {
    const response = await put<ApiResponse<CoaGroupDto>>(coaGroupEndpoints.update(id), data);
    return response.data;
  }

  /** Soft-delete; rowVersion is required for optimistic-concurrency. */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(coaGroupEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = coaGroupEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }

  /** ADR-0061 rev 3: the next group code in the subtype's block (e.g. 130101), or null when none. */
  async getNextCode(accountSubtypeId: ID): Promise<string | null> {
    const url = buildEndpointWithQuery(coaGroupEndpoints.nextCode(), { accountSubtypeId });
    const response = await get<ApiResponse<string | null>>(url);
    return response.data.data ?? null;
  }
}

export const coaGroupService = new CoaGroupService();
export default coaGroupService;
